import {
  FormEvent,
  memo,
  PropsWithChildren,
  useCallback,
  useState,
  useEffect,
  useMemo,
  useRef
} from 'react'
import { toJS, when } from 'mobx'
import {
  NavigateFunction,
  Route,
  Routes as ReactRouterRoutes
} from 'react-router-dom'
import { Form } from 'react-final-form'
import { FormSubscription } from 'final-form'
import Bugsnag from '@bugsnag/js'
import { TurnstileInstance } from '@marsidev/react-turnstile'
import { Typography, Link, Container } from '@mui/material'

import { executeAsync } from 'utils/performance'
import { trackPageView, trackTeal } from 'utils/tracking'
import {
  CreditChangeOption,
  TraversalError
} from 'interfaces/ApolloResponse.interface'
import VersionDisplay from 'components/VersionDisplay'
import TemporarySkeleton from 'components/SkeletonView/TemporarySkeleton'
import { getFooterSeals } from 'components/Footer/getSeals'
import Seals from 'components/Footer/Seals'
import CategoryLayout from 'components/Layout/Category'
import {
  setGlobalEventProperty,
  trackInAmplitude
} from 'utils/tracking/amplitude'
import { FooterPortalContainer } from 'components/Portal/Footer'
import ReturningVisitor from 'components/ReturningVisitor'
import { useClearError, useSetError } from 'context/Error/hook'
import { ConfigOptions } from 'config/config.interface'
import { getDisplayedPages, isRceEditConfig } from 'config/utils/config'
import Page from 'containers/Page'
import { useRootStore } from 'stores/utils/hooks/useRootStore'
import InitialSkeleton from 'components/SkeletonView/InitialSkeleton'
import {
  AmplitudeEvent,
  ErrorType,
  EventProperty
} from 'utils/tracking/amplitude/amplitude.interface'
import { AffiliateEventName, sendAffiliateEvent } from 'utils/affiliate'
import { useStores } from 'stores/utils/hooks/useStores'
import ErrorFallback from 'containers/ErrorFallback'
import { LoadingMode } from 'components/LoadingProvider'
import SSL from 'components/Footer/SSL'
import Divider from 'components/Footer/Divider'
import Benefits from 'components/Footer/Benefits'
import PageLayout from 'components/Layout/Page'
import ProgressIndicator from 'components/ProgressIndicator'
import DDFTrustCards from 'containers/Smava/DDFTrustCards'
import QueryParamsUpdater from 'containers/QueryParamsUpdater'
import { isTenantSmava } from 'utils/url'
import isNotMortgage from 'utils/formDependencies/conditions/isNotMortgage'
import {
  broadcastToParent,
  RegisteredEventName
} from '@finanzcheck/catalyst-pollard'
import { AxiosError } from 'axios'
import pollard from 'utils/pollard'
import { appEnvironment } from 'utils/env'
import InitialSmavaDDFSkeleton from 'components/SkeletonView/InitialSmavaDDFSkeleton'
import isNotAffiliate from 'utils/formDependencies/conditions/isNotAffiliate'
import { getPageNameFromPath, RCE_EDIT_PAGE_NAMES } from 'utils/pages'
import { useUrlParams } from 'stores/utils/hooks/useUrlParams'
import { PageName } from 'stores/helpers/Navigation.interface'
import { FormNavigationSwitcher } from 'components/FormNavigation'
import InactivityContainer from 'InactivityContainer'
import { DacFooterWrapper } from 'components/DigitalAccountCheck/DacPageComponents/DacFooterWrapper'
import { useAgeGroup } from 'stores/utils/hooks/useAgeGroup'
import { AgeGroup } from 'constants/components'
import Agb from 'components/Smava/RceEdit/AGB'
import { valueComparer } from 'utils/tracking/amplitude/utils'
import { CaptchaWidget } from 'components/CaptchaWidget'
import { useCaptcha } from 'hooks/useCaptcha'
import AgbRceEditFfg from 'components/AbTestComponents/CAT3598/AgbRceEditFfg'
import { logAndTrackError } from 'utils/log/logAndTrackError'
import { isPrefillPossible } from 'components/AbTestComponents/CAT3834/utils'
import { COOKIE_KEY_FCID, getCookie } from 'utils/cookie'
import { Experiments } from 'CustomOptimizelyProvider'
import { PossibleVariants } from 'utils/tracking/optimizely/types'
import { trackPremiumAndTimingEvents } from 'components/AbTestComponents/CAT4015/utils'

interface RouteProps {
  options: ConfigOptions | undefined
  navigate: NavigateFunction
}

const isAddUserPropertiesOnPut = isTenantSmava()

const RouteElement = memo(({ options, navigate }: RouteProps) => {
  if (!options) {
    return <>{ErrorFallback}</>
  }

  return <Page navigate={navigate} options={options} />
})
RouteElement.displayName = 'RouteElement'

const formSubscriptions: FormSubscription = {
  submitting: true,
  pristine: true
}

const MORTGAGE_REDIRECT_URL_HOST = 'https://baufi.smava.de'

type RoutesProps = {
  navigate: NavigateFunction
}

const Routes = ({ navigate }: PropsWithChildren<RoutesProps>) => {
  const { rootStore } = useRootStore()

  const isSmava = isTenantSmava()
  // string is deprecated, please use PageName
  const [currentPage, setCurrentPage] = useState<string | undefined | PageName>(
    undefined
  )

  const urlParams = useUrlParams()
  const ageGroup = useAgeGroup()
  const captchaRef = useRef<TurnstileInstance | null>(null)
  const { handleCaptcha, setToken, shouldAwaitCaptcha, shouldHandleCaptcha } =
    useCaptcha({
      captchaRef
    })

  const {
    experimentData,
    page: {
      config,
      isOnTermsAndConditionsPage,
      isLastDisplayedPage,
      getCurrentIndex
    },
    navigation: {
      goToNextPage,
      goToPreviousPage,
      parentUrl,
      currentPageInSession
    },
    captcha,
    traversal,
    prefill,
    fieldErrors,
    history
  } = useStores()

  const isInCAT3818Variation = config.name === 'ddf_rce_dac_choice'
  const isInCAT3834PrefillExperiment =
    experimentData[Experiments.CAT_3834_PREFILL] ===
    PossibleVariants.VARIATION_B
  const isLastPage = isLastDisplayedPage(currentPageInSession)

  const parentFrameStartLoadTimeCallback = (timestamp: number) => {
    trackInAmplitude(AmplitudeEvent.ParentFrameStartLoadTime, {
      timestamp
    })
  }

  useEffect(() => {
    pollard.listenTo(
      RegisteredEventName.parentFrameStartLoadDate,
      parentFrameStartLoadTimeCallback
    )
  }, [])

  useEffect(() => {
    Bugsnag.setContext(currentPage || '')
  }, [currentPage])

  useEffect(() => {
    if (!currentPage) {
      return
    }

    setGlobalEventProperty(EventProperty.CurrentPage, currentPage)
    trackInAmplitude(AmplitudeEvent.ViewPage)
    trackPageView(rootStore)
    sendAffiliateEvent(AffiliateEventName.Pageview, traversal, rootStore)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage])

  const handleCaptchaWidgetLoad = useCallback(() => {
    captcha.setCaptchaFlowStatus('loaded')
    captcha.setCaptchaRef(captchaRef)
  }, [shouldHandleCaptcha, captcha])

  const preventSubmit = useCallback((e: FormEvent) => {
    e.preventDefault()
  }, [])

  const initialValues = useMemo(() => toJS(traversal.data), [traversal.data])

  const setError = useSetError()
  const clearError = useClearError()

  history.listen(({ location }) => {
    const currentPageName = getPageNameFromPath(location.pathname)
    setCurrentPage(currentPageName)
    // TODO: Can be moved to NavigationContext later..
    clearError()
  })

  const getMortgageRedirectUrl = () => {
    if (!parentUrl) {
      return MORTGAGE_REDIRECT_URL_HOST
    }

    const url = new URL(parentUrl as string)
    const searchParams = new URLSearchParams(url.search)

    /* deleting amount category and term from the query since we will add it from the value in taurine */
    searchParams.delete('amount')
    searchParams.delete('category')
    searchParams.delete('duration')
    searchParams.delete('term')
    searchParams.delete('purpose')

    return `${MORTGAGE_REDIRECT_URL_HOST}?amount=${
      traversal.data.common.amount
    }&category=${traversal.data.common.purpose}&duration=${
      traversal.data.common.term
    }&${searchParams.toString()}`
  }

  const isNotAffiliateBoolean = isNotAffiliate(urlParams?.context)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = async (_: any, form: any) => {
    const shouldBroadcastRedirectEvent =
      isSmava &&
      !isNotMortgage(traversal.data, urlParams?.context) &&
      (currentPage === 'contact' || currentPage === 'base')

    trackInAmplitude(AmplitudeEvent.CtaIsClicked)

    rootStore.setLoadingMode(LoadingMode.Blocking)

    if (shouldBroadcastRedirectEvent) {
      broadcastToParent({
        eventName: RegisteredEventName.redirect,
        data: {
          url: getMortgageRedirectUrl()
        }
      })

      return null
    }

    const { data } = traversal
    const trackTplAmountAmountChanging =
      (data.debtors.primary.activeLoans?.length ||
        data.debtors.secondary.activeLoans?.length) &&
      data.common.manuallyAdaptedAmount &&
      data.common.amountAdaption === CreditChangeOption.other

    if (trackTplAmountAmountChanging) {
      const comparedValue = valueComparer(
        traversal.calculatedManuallyAdaptedAmount,
        data.common.manuallyAdaptedAmount
      )
      trackInAmplitude(AmplitudeEvent.TplManuallyAdaptedAmountRelation, {
        comparedValue
      })
    }
    try {
      const isOnTermsPage = await isOnTermsAndConditionsPage()

      if (isOnTermsPage) {
        /* calling traversal patch directly so that we can await before the next put call */
        await traversal.patch([
          { op: 'set', path: '/common/hasAcceptedTerms', value: true }
        ])
        traversal.acceptTermsAndConditions()
      }

      await fieldErrors.setFieldErrorsFromAxiosResponse(await traversal.put())

      const errors = await fieldErrors.getErrors(currentPage || '')
      const erroredPaths = Object.keys(errors)

      const previousErrorPageName =
        await fieldErrors.findFirstErrorPage(getCurrentIndex())

      if (erroredPaths.length > 0) {
        executeAsync(() => {
          trackInAmplitude(AmplitudeEvent.ValidateTraversal, {
            type: ErrorType.Page,
            page: currentPage,
            errors: erroredPaths.join(',')
          })

          for (const errorPath of erroredPaths) {
            const traversalErrors = errors[errorPath]
            if (!traversalErrors) {
              continue
            }

            trackInAmplitude(AmplitudeEvent.ValidateTraversal, {
              type: ErrorType.Field,
              path: errorPath,
              errors: traversalErrors
                .map(({ id }: TraversalError) => id)
                .join(',')
            })
          }
        })

        const visibleFieldPaths = form.getRegisteredFields()
        const visibleAndErrored = erroredPaths.filter(
          (x) => !visibleFieldPaths.includes(x)
        )

        visibleAndErrored.forEach((path) => delete errors[path])

        rootStore.setLoadingMode(LoadingMode.None)

        return errors
      }

      if (previousErrorPageName) {
        rootStore.setLoadingMode(LoadingMode.None)

        const errorsOnFirstBrokenPage = await fieldErrors.getErrors(
          previousErrorPageName
        )
        return errorsOnFirstBrokenPage
      }

      if (isAddUserPropertiesOnPut) {
        rootStore.traversal.setUserPropertiesOnPut()
      }

      if (isOnTermsPage) {
        trackPremiumAndTimingEvents(rootStore)
      }

      if (
        isOnTermsPage &&
        isInCAT3834PrefillExperiment &&
        traversal.traversalId &&
        data.debtors.count === 1 &&
        data.common.purpose !== 'PRE_OWNED_CAR' &&
        isPrefillPossible(traversal.traversalId)
      ) {
        trackInAmplitude(AmplitudeEvent.PrefillInitiated)
        const {
          system: { advertisementId }
        } = data

        try {
          await prefill.getIsPrefillAvailable(
            advertisementId,
            traversal.traversalId
          )

          trackTeal({
            transaction: {
              event: AmplitudeEvent.PrefillCheck
            },
            payload: {
              eligible: true
            },
            context: {
              Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
              userAttributes: {
                advertisementId
              }
            }
          })
        } catch (err) {
          const errorObj = err as any

          trackTeal({
            transaction: {
              event: AmplitudeEvent.PrefillCheck
            },
            payload: {
              eligible: false,
              timeout: errorObj.timeout || undefined,
              errStatus: errorObj.status || 'unknown'
            },
            context: {
              Optimizely: { userId: getCookie(COOKIE_KEY_FCID) },
              userAttributes: {
                advertisementId
              }
            }
          })
        }
      }

      if (shouldHandleCaptcha('ctaClick')) {
        handleCaptcha('ctaClick')

        if (shouldAwaitCaptcha()) {
          try {
            await when(
              () =>
                captcha.captchaFlowStatus === 'success' ||
                captcha.captchaFlowStatus === 'error',
              {
                timeout: 120000
              }
            )
          } catch (err) {
            logAndTrackError(new Error('Captcha timeout or error'))
            trackInAmplitude(AmplitudeEvent.CaptchaTimeoutError, {
              error: String(err),
              errObject: err
            })
          }
        }
      }

      await goToNextPage(navigate)
    } catch (error) {
      rootStore.setLoadingMode(LoadingMode.None)
      const axiosError = error as AxiosError
      setError(axiosError)
      trackInAmplitude(AmplitudeEvent.ErrorOccurred, {
        error: axiosError?.message
      })
    }

    return {}
  }

  const isRCEEditPage = isRceEditConfig(config.name)

  const isDacFooterWrapperStep = isRCEEditPage && currentPageInSession === 'dac'

  const isProgressIndictorDisplayed =
    !isDacFooterWrapperStep &&
    currentPageInSession !== 'brandTracking' &&
    !RCE_EDIT_PAGE_NAMES.includes(currentPageInSession || '')

  const areDDFTrustCardsDisplayed =
    isSmava &&
    !isDacFooterWrapperStep &&
    !RCE_EDIT_PAGE_NAMES.includes(currentPageInSession || '')

  const isAgbShownAtBottomOfPage = currentPageInSession === 'editPage'

  const isDacFooterWrapperDisplayed =
    isSmava &&
    ((isDacFooterWrapperStep && ageGroup !== AgeGroup.YOUNG_AGE_GROUP) ||
      isRCEEditPage)

  return (
    <>
      <CaptchaWidget
        ref={captchaRef}
        onWidgetLoad={handleCaptchaWidgetLoad}
        onSuccess={setToken}
      />
      {!config.version || !currentPage ? (
        isSmava ? (
          <InitialSmavaDDFSkeleton />
        ) : (
          <InitialSkeleton />
        )
      ) : (
        <>
          {isNotAffiliateBoolean && isSmava && <QueryParamsUpdater />}
          <Form
            subscription={formSubscriptions}
            initialValues={initialValues}
            validateOnBlur
            // TODO: Refactor `onSubmit` method and add tests.
            onSubmit={onSubmit}
          >
            {({ handleSubmit, form }) => {
              traversal.setFormApi(form)

              return (
                <PageLayout onSubmit={preventSubmit}>
                  {isProgressIndictorDisplayed && (
                    <ProgressIndicator
                      withMotivationalText={!isSmava}
                      isSlimProgress={isSmava}
                      isWithMotivationalTextBelow={isSmava}
                    />
                  )}
                  <ReturningVisitor />
                  <ReactRouterRoutes>
                    {getDisplayedPages(rootStore).map(([pageName, options]) => (
                      <Route
                        key={pageName}
                        path={pageName}
                        element={
                          // We have to make sure to use the same navigate instance all over the app
                          <RouteElement options={options} navigate={navigate} />
                        }
                      />
                    ))}
                    <Route path="*" element={<TemporarySkeleton />} />
                  </ReactRouterRoutes>
                  {isLastPage && (
                    <InactivityContainer handleSubmit={handleSubmit} />
                  )}

                  <FormNavigationSwitcher
                    isSmava={isSmava}
                    onNext={handleSubmit}
                    onBack={() => goToPreviousPage(navigate)}
                    navigate={navigate}
                    isOfferLabel={isLastPage}
                  />

                  {isDacFooterWrapperDisplayed && (
                    <CategoryLayout>
                      <DacFooterWrapper />
                    </CategoryLayout>
                  )}
                  <FooterPortalContainer />
                  {isAgbShownAtBottomOfPage ? (
                    isSmava ? (
                      <Agb />
                    ) : (
                      <AgbRceEditFfg />
                    )
                  ) : null}
                </PageLayout>
              )
            }}
          </Form>
          {!isSmava && !isRCEEditPage && !isInCAT3818Variation && (
            <>
              <SSL />
              <Divider />
              <Benefits />
              <Divider />
              <Seals
                seals={getFooterSeals(!isNotAffiliateBoolean)}
                itemMargin={16}
              />
            </>
          )}
          {isInCAT3818Variation && ageGroup !== AgeGroup.OLD_AGE_GROUP && (
            <>
              <Seals
                seals={getFooterSeals(!isNotAffiliateBoolean)}
                itemMargin={16}
              />
              <Container
                sx={{
                  maxWidth: '460px !important', // we seem to have setting in the theme for this ;o(
                  padding: 1
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  Ich bin ausdrücklich einverstanden, dass FINANZCHECK meine
                  Kontoauszüge (inkl. enthaltener besonderer Kategorien
                  personenbezogener Daten) erhebt und zur Kreditprüfung an
                  mögliche Kreditgeber weiterleitet. Ich kann meine Einwilligung
                  jederzeit widerrufen.{' '}
                  <Link href="/uber-uns/datenschutz/">
                    Unsere Datenschutzhinweise.
                  </Link>
                </Typography>
              </Container>
            </>
          )}
          {areDDFTrustCardsDisplayed && (
            <DDFTrustCards isNotAffiliate={isNotAffiliateBoolean} />
          )}
          {config.rateHint && <config.rateHint />}
          {appEnvironment !== 'production' && <Divider />}
          <VersionDisplay />
        </>
      )}
    </>
  )
}

export default memo(Routes)
